import { useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, Loader2, Minus, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiError } from "../lib/api";
import { useCart } from "../context/CartContext";

// Standard statewide base sales-tax rates (2026). Final tax incl. local rates is
// calculated authoritatively by Stripe Tax on the hosted checkout page.
const STATE_TAX_RATES = {
  AL: 0.04, AK: 0, AZ: 0.056, AR: 0.065, CA: 0.0725, CO: 0.029, CT: 0.0635, DE: 0,
  FL: 0.06, GA: 0.04, HI: 0.04, ID: 0.06, IL: 0.0625, IN: 0.07, IA: 0.06, KS: 0.065,
  KY: 0.06, LA: 0.0445, ME: 0.055, MD: 0.06, MA: 0.0625, MI: 0.06, MN: 0.06875, MS: 0.07,
  MO: 0.04225, MT: 0, NE: 0.055, NV: 0.0685, NH: 0, NJ: 0.06625, NM: 0.04875, NY: 0.04,
  NC: 0.0475, ND: 0.05, OH: 0.0575, OK: 0.045, OR: 0, PA: 0.06, RI: 0.07, SC: 0.06,
  SD: 0.042, TN: 0.07, TX: 0.0625, UT: 0.061, VT: 0.06, VA: 0.053, WA: 0.065, WV: 0.06,
  WI: 0.05, WY: 0.04, DC: 0.06,
};

export default function CartPage() {
  const { items, setQty, remove, subtotal, clear } = useCart();
  const [form, setForm] = useState({ name: "", email: "", line1: "", city: "", state: "", postal_code: "", country: "US" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const checkout = async () => {
    if (!form.name || !form.email || !form.line1 || !form.city || !form.postal_code) {
      toast.error("Please fill in your name, email and shipping address");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        items: items.map((i) => ({ product_id: i.product.id, quantity: i.qty })),
        customer: form,
        origin_url: window.location.origin,
      });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e, "Could not start checkout"));
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="mx-auto flex max-w-2xl flex-col items-center px-4 py-24 text-center" data-testid="cart-empty">
        <p className="font-heading text-2xl font-semibold text-white">Your cart is empty</p>
        <p className="mt-2 text-slate-400">Trends wait for no one.</p>
        <Link to="/shop" className="btn-gold mt-6" data-testid="cart-empty-shop-btn">Browse trending products</Link>
      </div>
    );

  const stateCode = (form.state || "").trim().toUpperCase().slice(0, 2);
  const taxRate = STATE_TAX_RATES[stateCode];
  const taxEstimate = taxRate != null ? subtotal * taxRate : null;

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-8" data-testid="cart-page">
      <h1 className="font-heading text-3xl font-bold text-white">Cart & checkout</h1>
      <div className="mt-8 grid gap-8 lg:grid-cols-5">
        <div className="space-y-4 lg:col-span-3">
          {items.map(({ product: p, qty }) => (
            <div key={p.id} className="card-surface flex gap-4 p-4" data-testid={`cart-item-${p.id}`}>
              <img src={p.images[0]} alt={p.name} className="h-24 w-24 rounded-xl object-cover" />
              <div className="flex flex-1 flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-white">{p.name}</p>
                    <p className="text-xs text-slate-500">{p.category}</p>
                  </div>
                  <button data-testid={`cart-remove-${p.id}`} onClick={() => remove(p.id)} className="text-slate-500 hover:text-rose-400">
                    <Trash2 size={16} />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-2">
                  <div className="flex items-center rounded-full border border-white/15">
                    <button data-testid={`cart-qty-minus-${p.id}`} onClick={() => setQty(p.id, qty - 1)} className="px-3 py-1.5 text-slate-300"><Minus size={13} /></button>
                    <span className="w-7 text-center text-sm font-semibold text-white">{qty}</span>
                    <button data-testid={`cart-qty-plus-${p.id}`} onClick={() => setQty(p.id, Math.min(20, qty + 1))} className="px-3 py-1.5 text-slate-300"><Plus size={13} /></button>
                  </div>
                  <p className="font-heading font-bold text-white">{fmt(p.price * qty)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-2">
          <div className="card-surface sticky top-24 p-6">
            <p className="font-heading text-lg font-semibold text-white">Shipping & payment</p>
            <div className="mt-4 space-y-3">
              <input data-testid="checkout-name" className="input-dark" placeholder="Full name" value={form.name} onChange={set("name")} />
              <input data-testid="checkout-email" className="input-dark" type="email" placeholder="Email (order updates go here)" value={form.email} onChange={set("email")} />
              <input data-testid="checkout-line1" className="input-dark" placeholder="Street address" value={form.line1} onChange={set("line1")} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="checkout-city" className="input-dark" placeholder="City" value={form.city} onChange={set("city")} />
                <input data-testid="checkout-state" className="input-dark" placeholder="State" value={form.state} onChange={set("state")} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="checkout-zip" className="input-dark" placeholder="ZIP / postal code" value={form.postal_code} onChange={set("postal_code")} />
                <input data-testid="checkout-country" className="input-dark" placeholder="Country" value={form.country} onChange={set("country")} />
              </div>
            </div>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-sm">
              <div className="flex justify-between text-slate-400">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{fmt(subtotal)}</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Shipping</span>
                <span className="text-emerald-400">Free (direct from supplier)</span>
              </div>
              <div className="flex justify-between text-slate-400">
                <span>Est. sales tax{taxRate != null ? ` (${stateCode} ${(taxRate * 100).toFixed(2)}%)` : ""}</span>
                <span data-testid="cart-tax-estimate">{taxEstimate != null ? fmt(taxEstimate) : "Enter state"}</span>
              </div>
              {taxEstimate != null && (
                <div className="flex justify-between font-semibold text-white">
                  <span>Estimated total</span>
                  <span data-testid="cart-total-estimate">{fmt(subtotal + taxEstimate)}</span>
                </div>
              )}
              <p className="text-[11px] text-slate-600">Final tax (incl. local rates) is calculated by Stripe at checkout.</p>
            </div>
            <button
              data-testid="checkout-submit-btn"
              onClick={checkout}
              disabled={loading}
              className="btn-gold mt-5 w-full disabled:opacity-60"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : <CreditCard size={17} />}
              Pay {fmt(subtotal)} with Stripe
            </button>
            <p className="mt-3 text-center text-[11px] text-slate-600">
              After payment your order is auto-placed with the manufacturer. Test card: 4242 4242 4242 4242
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
